import { Link } from "react-router-dom";
import { ArrowRight, Check, Flame, Bandage, Link2, Replace, ClipboardCheck, Plus } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";

const ICONS = {
  hot: Flame,
  cold: Bandage,
  splice: Link2,
  replace: Replace,
  maintenance: ClipboardCheck,
};

const Services = () => {
  const { t } = useLang();

  return (
    <div data-testid="services-page">
      <Seo pageKey="services" />
      {/* Header */}
      <section className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative mx-auto max-w-[1400px] px-5 lg:px-10 pt-20 lg:pt-28 pb-16 lg:pb-24">
          <div className="flex items-center gap-3 mb-6">
            <span className="block w-10 h-px bg-[#FF4500]" />
            <span className="text-overline text-[#FF4500]">{t.services.eyebrow}</span>
          </div>
          <h1
            data-testid="services-title"
            className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.9] text-white max-w-5xl whitespace-pre-line"
          >
            {t.services.title}
          </h1>
          <p className="mt-8 text-white/70 max-w-3xl leading-relaxed">{t.services.sub}</p>
        </div>
      </section>

      {/* Services List */}
      <section className="bg-[#050505]">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16 lg:py-24">
          <div className="grid grid-cols-1 gap-px bg-white/10 border border-white/10">
            {t.services.list.map((s, idx) => {
              const Icon = ICONS[s.id] || Check;
              const reverse = idx % 2 === 1;
              return (
                <div
                  key={s.id}
                  data-testid={`service-item-${s.id}`}
                  className={`bg-[#0a0a0a] grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 p-8 lg:p-12 hover:bg-[#0f0f0f] transition-colors ${
                    reverse ? "lg:[direction:rtl]" : ""
                  }`}
                >
                  <div className={`lg:col-span-3 ${reverse ? "lg:[direction:ltr]" : ""}`}>
                    <div className="font-display text-7xl lg:text-8xl text-[#FF4500] leading-none">
                      {s.n}
                    </div>
                    <Icon className="w-8 h-8 mt-6 text-white/70" />
                  </div>
                  <div className={`lg:col-span-9 ${reverse ? "lg:[direction:ltr]" : ""}`}>
                    <h3 className="font-display text-3xl lg:text-5xl uppercase text-white leading-[0.95]">
                      {s.t}
                    </h3>
                    <p className="mt-5 text-white/70 leading-relaxed max-w-3xl">{s.d}</p>
                    <ul className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
                      {s.bullets.map((b) => (
                        <li
                          key={b}
                          className="flex items-start gap-2 text-sm text-white/80 p-3 border border-white/10 bg-[#050505]"
                        >
                          <Check className="w-4 h-4 mt-0.5 text-[#FF4500] flex-none" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Extra services */}
          <div className="mt-10 p-8 lg:p-10 border border-white/10 bg-[#0a0a0a] flex flex-col lg:flex-row items-start gap-6">
            <Plus className="w-8 h-8 text-[#FF4500] flex-none" />
            <div>
              <h4 className="font-display text-2xl uppercase text-white">
                {t.services.extra.t}
              </h4>
              <p className="mt-2 text-white/70 leading-relaxed">{t.services.extra.d}</p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-14 flex flex-wrap items-center justify-between gap-6 p-8 lg:p-10 border border-[#FF4500]/40 bg-[#0a0a0a]">
            <div>
              <p className="text-overline text-[#FF4500] mb-2">{t.common.tagline}</p>
              <h3 className="font-display text-3xl lg:text-4xl uppercase text-white">
                {t.nav.quote}
              </h3>
            </div>
            <Link
              to="/contact"
              data-testid="services-cta"
              className="inline-flex items-center gap-3 bg-[#FF4500] hover:bg-[#FF5722] text-black px-6 py-4 font-bold uppercase tracking-widest text-sm group"
            >
              {t.nav.contact}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
